import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { useNavigate, useLocation } from 'react-router';
import { useSocket } from '@/providers/socket-io';
import { CONFIRMATION_TYPES, MEETING_REASON_TYPES, SOCKET_EVENTS, CALL_SERVICE_TYPES, CALL_STATE_TYPES, MEETING_TYPE, CONNECTION_STATUS } from '@/constants/socket.ts';
import { NavigationPath } from '@/router/navigation-path.ts';
import { formatCustomDate, jsonTryParse, parseJwt } from '@/utility';
import { Button, DialogModal, MeetCamera, MeetToast } from '@/components';
import { CallServiceType, CallStateType } from '@/types';
import { MeetAppointmentToastTypes, MeetingContextType, PastMeetingModalTextsTypes, StudentJoinedEventPayload } from './meeting-types';
import { MeetCameraHandle } from '@/components/meet-camera';
import { useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useApi } from '@/api/useApi.tsx';
import { formatMeetingData } from '@/utility/formatters/meetingFormatter.ts';
import { useAuthStore } from '@/zustand/auth/store.ts';
import NotificationManager from '@/managers/notification-manager.tsx';
import NoStudentIcon from '@/assets/images/no-student-icon.png';
import MeetAppointmentToast from '@/components/meet-appointment-toast';
import LeaveCountdownButton from '@/components/leaveCountdownButton';
import { useQueryClient } from 'react-query';
import videoCameraRecord from '@/assets/icons/videocamera-record.svg';
import dayjs from 'dayjs';
import { AppointmentCreateRequestLiveServiceTypeEnum } from '@dopingtech/live_expert_bff_api_ts';
import { ExpertServiceClientResponseExpertServiceStatusEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import { SERVICES_DETAIL_LIST_FOR_COACHING, SERVICES_DETAIL_LIST_FOR_GOT_IT } from '@/constants';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useDevicePermissions } from '@/context/device-permission-context.tsx';

const MeetingContext = createContext<MeetingContextType | null>(null);

const TIMEZONE = 'Europe/Istanbul';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault(TIMEZONE);

export const useMeeting = (): MeetingContextType => {
	const context = useContext(MeetingContext);
	if (!context) {
		throw new Error('useMeeting must be used within a MeetingProvider');
	}
	return context;
};

export const MeetingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { data: authData } = useAuthStore();

	const { socket, on, emit, off } = useSocket();
	const [callState, setCallState] = useState<CallStateType>(CALL_STATE_TYPES.DEFAULT);

	const { controlPermission } = useDevicePermissions();

	const [meetingType, setMeetingType] = useState<string | null>(null);
	const [isAcceptedCall, setIsAcceptedCall] = useState<boolean>(false);
	const [isAcceptedCallLoading, setIsAcceptedCallLoading] = useState<boolean>(false);
	const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
	const navigate = useNavigate();
	const location = useLocation();
	const queryClient = useQueryClient();

	const [socketData, setSocketData] = useState<any>({});
	const [connectionApp, setConnectionApp] = useState<CallServiceType>('COACHING');
	const meetCameraRef = useRef<MeetCameraHandle | null>(null);
	const meetAppointmentToastRef = useRef<MeetAppointmentToastTypes>(null);
	const [isConnectToastVisible, setIsConnectToastVisible] = useState<boolean>(false);
	const [isAppointmentConnectToastVisible, setIsAppointmentConnectToastVisible] = useState<boolean>(false);
	const [isAppointmentAvailable, setIsAppointmentAvailable] = useState<boolean>(false);
	const [activeAppointment, setActiveAppointment] = useState<any>(null);
	const [showPastMeetingModal, setShowPastMeetingModal] = useState<boolean>(false);
	const [appointmentInformation, setAppointmentInformation] = useState<any>(null);
	const [hasActiveMeeting, setHasActiveMeeting] = useState<boolean>(false);
	const [showStudentNotConnectModal, setStudentNotConnectModal] = useState<boolean>(false);
	const [studentWaitButNotConnectModal, setStudentWaitButNotConnectModal] = useState<boolean>(false);
	const [studentConnectedModal, setStudentConnectedModal] = useState<boolean>(false);
	const [rejectedPastMeeting, setRejectedPastMeeting] = useState<boolean>(false);

	const [studentWaitButNotConnectStudentId, setStudentWaitButNotConnectStudentId] = useState<number | null>(null);
	const [connectedUserData, setConnectedUserData] = useState<StudentJoinedEventPayload | null>(null);

	const [pastMeetingModalTexts, setPastMeetingModalTexts] = useState<PastMeetingModalTextsTypes>({
		title: 'Görüşmen Yarıda Kaldı',
		description: 'Bağlan butonuna basarak görüşmeye devam edebilirsin.'
	});

	const socketDataRef = useRef<any>({});
	const callStateRef = useRef<any>({});
	const isReCallRef = useRef<any>({});
	const showStudentNotConnectModalRef = useRef<any>(false);
	const meetingTypeRef = useRef<string>();

	const isApplicant = useMemo(() => {
		if (authData?.access_token) {
			return parseJwt(authData?.access_token)?.resource_access?.['live-expert-applicant']?.roles?.includes('Expert_Applicant');
		}

		return false;
	}, [authData?.access_token]);

	useEffect(() => {
		console.log('Socket Data : ', {
			socketData,
			callState,
			isAcceptedCall,
			isConnectToastVisible,
			connectionApp,
			meetingType,
			timestamp: new Date().toISOString()
		});
		socketDataRef.current = socketData;
	}, [socketData]);

	useEffect(() => {
		callStateRef.current = callState;
	}, [callState]);

	useEffect(() => {
		meetingTypeRef.current = meetingType;
	}, [meetingType]);

	const userId = authData?.access_token ? parseJwt(authData?.access_token)?.sub : '';

	const { MeetingControllerApi, AppointmentControllerApi, ExpertControllerApi } = useApi();

	const { data: expertDetailInfoData } = useQueryProvider(
		[QUERY_KEYS.getLoggedInExpertDetailInfo, userId],
		() => ExpertControllerApi.getLoggedInExpertDetailInfo(),
		{
			select: ({ data }) => data.data,
			enabled: !!userId && !isApplicant,
			refetchOnWindowFocus: false
		}
	);

	const ownedServices = useMemo(() => {
		const oneOnOneLecture = expertDetailInfoData?.serviceList?.find(
			(x: any) => x.liveServiceTypeId === SERVICES_DETAIL_LIST_FOR_GOT_IT.GOT_IT_APPOINTMENTS && x.expertServiceStatus === ExpertServiceClientResponseExpertServiceStatusEnum.Approved
		);
		const isOneOnOneCoach = expertDetailInfoData?.serviceList?.find(
			(x: any) => x.liveServiceTypeId === SERVICES_DETAIL_LIST_FOR_COACHING.COACHING_APPOINTMENTS && x.expertServiceStatus === ExpertServiceClientResponseExpertServiceStatusEnum.Approved
		);
		return {
			oneOnOneLecture: !!oneOnOneLecture,
			oneOnOneCoaching: !!isOneOnOneCoach
		};
	}, [expertDetailInfoData]);

	const { data: activeMeeting } = useQueryProvider([QUERY_KEYS.getActiveMeeting], () => MeetingControllerApi.getActiveMeeting(), {
		select: ({ data }) => data.data,
		cacheTime: 0,
		refetchOnWindowFocus: true,
		refetchOnMount: 'always',
		enabled: !isApplicant
	});

	const { data: todaysCoachAppointments } = useQueryProvider(
		[QUERY_KEYS.findAppointmentsOfTheDay, dayjs().tz(TIMEZONE).format('YYYY-MM-DD'), 'CREATED', AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching],
		() =>
			AppointmentControllerApi.findAppointmentsOfTheDay(dayjs().tz(TIMEZONE).format('YYYY-MM-DD'), 'CREATED', AppointmentCreateRequestLiveServiceTypeEnum.OneOnOneCoaching, undefined, 200, ['appointmentStartDate,ASC']),
		{
			select: ({ data }) => data.data,
			refetchInterval: 60000,
			refetchOnWindowFocus: true,
			refetchOnMount: 'always',
			enabled: !isApplicant && ownedServices.oneOnOneCoaching
		}
	);

	// TODO: OE-10507 - PLANNED_LECTURE appointment check will be re-enabled after service type gating is implemented
	// const { data: todaysLectureAppointments } = useQueryProvider(
	// 	[QUERY_KEYS.findAppointmentsOfTheDay, dayjs().tz(TIMEZONE).format('YYYY-MM-DD'), 'CREATED', AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture],
	// 	() =>
	// 		AppointmentControllerApi.findAppointmentsOfTheDay(dayjs().tz(TIMEZONE).format('YYYY-MM-DD'), 'CREATED', AppointmentCreateRequestLiveServiceTypeEnum.PlannedLecture, undefined, 200, ['appointmentStartDate,ASC']),
	// 	{
	// 		select: ({ data }) => data.data,
	// 		refetchInterval: 60000,
	// 		refetchOnWindowFocus: true,
	// 		refetchOnMount: 'always',
	// 		enabled: !isApplicant && ownedServices.oneOnOneLecture
	// 	}
	// );
	const todaysLectureAppointments = undefined;

	const offStatesExcept = (listOfStatesToKeepOn: string[]) => {
		const alwaysOnEvents = [SOCKET_EVENTS.ON.STUDENT_JOINED];

		for (const event of Object.values(SOCKET_EVENTS.ON)) {
			if (!listOfStatesToKeepOn.includes(event) && !alwaysOnEvents.includes(event)) {
				off(event);
			}
		}
	};

	useEffect(() => {
		const isCallPage = location?.pathname === NavigationPath.coachInstantMeetings || location?.pathname === NavigationPath.gotItInstantMeetings;
		if (!isCallPage && callState !== CALL_STATE_TYPES.DEFAULT && callState !== CALL_STATE_TYPES.CONNECT) {
			setIsConnectToastVisible(true);
		} else {
			setIsConnectToastVisible(false);
		}

		const isReviewPage = location?.pathname === NavigationPath.gotItMeetCompleted || location?.pathname === NavigationPath.coachMeetCompleted;

		if (isReviewPage) {
			setIsAppointmentConnectToastVisible(false);
		} else {
			setIsAppointmentConnectToastVisible(true);
		}
	}, [location, callState]);

	useEffect(() => {
		if (socket) {
			socket?.on(SOCKET_EVENTS.ON.BUSINESS_ERROR, data => {
				const dataParsed = jsonTryParse(data);

				setIsSearchLoading(false);

				const isRequestTracked = socketData?.activeRequestIds?.includes(dataParsed?.requestId) ?? false;

				if (dataParsed?.exceptionCode === 'LPM111') {
					amplitude.track('got_it_match_no_response_limit', {
						errorCode: 'LPM111'
					});
				}

				if (dataParsed?.exceptionMessage && isRequestTracked) {
					NotificationManager.notifySaveSlotChangesError(dataParsed.exceptionMessage);
				}
			});
		}

		return () => {
			socket?.off?.(SOCKET_EVENTS.ON.BUSINESS_ERROR);
		};
	}, [socketData]);

	useEffect(() => {
		const now = dayjs();

		const findActive = (appointments: any) =>
			appointments?.content?.find((appointment: any) => {
				const startTime = dayjs(appointment.appointmentStartDate);
				const endTime = dayjs(appointment.appointmentEndDate);
				return now.isAfter(startTime.subtract(15, 'minutes')) && now.isBefore(endTime);
			});

		const activeOrUpcomingAppointment = findActive(todaysLectureAppointments) ?? findActive(todaysCoachAppointments);

		if (activeOrUpcomingAppointment && (!hasActiveMeeting || rejectedPastMeeting)) {
			setActiveAppointment({
				appointmentId: activeOrUpcomingAppointment.appointmentId,
				appointmentStartDate: activeOrUpcomingAppointment.appointmentStartDate,
				appointmentEndDate: activeOrUpcomingAppointment.appointmentEndDate,
				studentName: activeOrUpcomingAppointment.studentName,
				studentSurname: activeOrUpcomingAppointment.studentSurname,
				studentGradeName: activeOrUpcomingAppointment.studentGradeName,
				avatarUrl: activeOrUpcomingAppointment.avatarUrl,
				interactionId: activeOrUpcomingAppointment.interactionId
			});
			setIsAppointmentAvailable(true);
		} else {
			setActiveAppointment(null);
			setIsAppointmentAvailable(false);
		}
	}, [todaysLectureAppointments, todaysCoachAppointments, hasActiveMeeting]);

	const resetSocketData = () => {
		setSocketData({
			handleMatch: {},
			meetingCreated: {},
			matchApproved: {},
			connectionData: {},
			requestIds: [],
			matchDeadline: null
		});
	};

	const initAllState = () => {
		setCallState(CALL_STATE_TYPES.DEFAULT);
		callStateRef.current = CALL_STATE_TYPES.DEFAULT;
		setIsAcceptedCall(false);
		setIsAcceptedCallLoading(false);
		setIsSearchLoading(false);
		setHasActiveMeeting(false);
		resetSocketData();
	};

	const addRequestId = (newRequestId: string) => {
		setSocketData((prevState: any) => ({
			...prevState,
			activeRequestIds: [...(prevState.activeRequestIds || []), newRequestId]
		}));
	};

	const socketCallback = {
		handleUserQueued: (_data: any) => {
			const data = jsonTryParse(_data);
			if (data?.success) {
				offStatesExcept([SOCKET_EVENTS.ON.MATCH_PROPOSED, SOCKET_EVENTS.ON.USER_QUEUED]);
				setIsSearchLoading(false);
				setCallState(CALL_STATE_TYPES.SEARCHING);
				setIsAcceptedCall(false);
				setIsAcceptedCallLoading(false);
				on(SOCKET_EVENTS.ON.MATCH_PROPOSED, socketCallback.handleMatchProposed);
			}
		},
		handleMatchApproved: (data: any) => {
			const dataParsed = jsonTryParse(data);
			setSocketData((prevState: any) => ({
				...prevState,
				matchApproved: dataParsed
			}));
		},
		handleMeetingCreated: (data: any) => {
			setIsAcceptedCall(true);
			setIsAcceptedCallLoading(false);
			setSocketData((prevState: any) => ({
				...prevState,
				meetingCreated: jsonTryParse(data)
			}));
			initConnectedListener();
		},
		handleMeetingEnded: (data: any) => {
			setIsAppointmentAvailable(false);
			setActiveAppointment(null);
			setStudentConnectedModal(false);
			setRejectedPastMeeting(false);

			queryClient.invalidateQueries([QUERY_KEYS.findAppointmentsOfTheDay, dayjs().format('YYYY-MM-DD'), 'CREATED']);

			const dataParsed = jsonTryParse(data);
			const connectionStatus = dataParsed?.meetingParticipantList?.find(x => x.participantType === 'EXPERT')?.connectionStatus;
			const studentId = dataParsed?.meetingParticipantList?.find(x => x.participantType === 'STUDENT')?.participantId;

			if (callStateRef.current === CALL_STATE_TYPES.CONNECT) {
				const navigationPath = connectionApp === CALL_SERVICE_TYPES.COACHING ? NavigationPath.coachMeetCompleted : NavigationPath.gotItMeetCompleted;

				navigate(navigationPath, {
					state: {
						socketData: socketDataRef.current
					}
				});

				offStatesExcept([]);
				initAllState();
				return;
			}

			if (connectionStatus && connectionStatus === CONNECTION_STATUS.NOT_CONNECTED && dataParsed?.meetingType === MEETING_TYPE.APPOINTMENT) {
				setStudentWaitButNotConnectModal(true);
				setStudentWaitButNotConnectStudentId(studentId);
				initAllState();

				return;
			}

			if (callStateRef.current === CALL_STATE_TYPES.CONNECT && showStudentNotConnectModalRef.current) {
				let navigationPath;

				if (meetingTypeRef.current !== MEETING_TYPE.INSTANT) {
					navigationPath = NavigationPath.coachAppointments;
				} else {
					navigationPath = connectionApp === CALL_SERVICE_TYPES.COACHING ? NavigationPath.coachInstantMeetings : NavigationPath.gotItInstantMeetings;
				}

				navigate(navigationPath);
				initAllState();

				if (isReCallRef.current) {
					handleSearchUser({ serviceGroup: connectionApp });
				}

				isReCallRef.current = false;

				setStudentNotConnectModal(false);
				showStudentNotConnectModalRef.current = false;

				return;
			}

			if (callStateRef.current === CALL_STATE_TYPES.FOUND && hasActiveMeeting) {
				const navigationPath = connectionApp === CALL_SERVICE_TYPES.COACHING ? NavigationPath.coachMeetCompleted : NavigationPath.gotItMeetCompleted;

				navigate(navigationPath);
				initAllState();
				return;
			}

			if (callStateRef.current === CALL_STATE_TYPES.FOUND && !hasActiveMeeting) {
				const navigationPath = connectionApp === CALL_SERVICE_TYPES.COACHING ? NavigationPath.coachInstantMeetings : NavigationPath.gotItInstantMeetings;

				navigate(navigationPath);
				initAllState();
				return;
			}

			if (socketData?.connectionData?.roomId) {
				initAllState();
				return;
			}

			setCallState(CALL_STATE_TYPES.DEFAULT);
			offStatesExcept([]);

			const navigationPath = connectionApp === CALL_SERVICE_TYPES.COACHING ? NavigationPath.coachMeetCompleted : NavigationPath.gotItMeetCompleted;

			navigate(navigationPath, {
				state: {
					socketData: socketDataRef.current
				}
			});
		},
		handleMeetingTerminationRequested: (data: any) => {
			handleEmitWithRequestTracking(data);
		},
		handleMatchProposed: (data: any) => {
			const handleMatchData = jsonTryParse(data) || {};
			const duration = handleMatchData?.matchingAcceptDuration;
			setSocketData((prevState: any) => ({
				...prevState,
				handleMatch: handleMatchData,
				matchDeadline: duration ? Date.now() + duration : null
			}));

			setCallState(CALL_STATE_TYPES.FOUND);
		}
	};

	const handleEmitWithRequestTracking = (data: any) => {
		try {
			const decoder = new TextDecoder('utf-8');
			const jsonString = decoder.decode(data);
			const objectData = jsonTryParse(jsonString);

			if (objectData?.data?.requestId) {
				addRequestId(objectData.data.requestId);
			}
		} catch (error) {
			console.error('Data Conversion Error:', error);
		}
	};

	const handleSearchUser = ({ serviceGroup }: { serviceGroup: CallServiceType }) => {
		initAllState();
		setIsSearchLoading(true);

		emit(SOCKET_EVENTS.EMIT.SEARCH_STARTED, { serviceGroup }, handleEmitWithRequestTracking);

		setConnectionApp(serviceGroup);
		offStatesExcept([SOCKET_EVENTS.ON.USER_QUEUED, SOCKET_EVENTS.ON.MATCH_PROPOSED]);

		on(SOCKET_EVENTS.ON.USER_QUEUED, data => socketCallback.handleUserQueued(data));

		on(SOCKET_EVENTS.ON.MATCH_PROPOSED, socketCallback.handleMatchProposed);
	};

	const onApprove = () => {
		if (isAcceptedCallLoading) {
			return;
		}

		setIsAcceptedCallLoading(true);

		emit(
			SOCKET_EVENTS.EMIT.MATCH_CONFIRMED,
			{
				confirmationType: CONFIRMATION_TYPES.ACCEPTED
			},
			handleEmitWithRequestTracking
		);

		on(SOCKET_EVENTS.ON.MATCH_APPROVED, (data: any) => socketCallback.handleMatchApproved(data));

		on(SOCKET_EVENTS.ON.MEETING_CREATED, (data: any) => socketCallback.handleMeetingCreated(data));
	};

	const initConnectedListener = () => {
		off(SOCKET_EVENTS.ON.MEETING_ENDED);
		on(SOCKET_EVENTS.ON.MEETING_ENDED, socketCallback.handleMeetingEnded);

		on(SOCKET_EVENTS.ON.MEETING_STARTED, data => {
			const dataParsed = jsonTryParse(data);
			meetCameraRef.current?.onEndDateUpdate(dataParsed?.endDate);
		});
	};

	const onStartCall = (isAppointment = false) => {
		offStatesExcept([SOCKET_EVENTS.ON.MEETING_ENDED]);
		initConnectedListener();
		setCallState(CALL_STATE_TYPES.CONNECT);
		setHasActiveMeeting(true);
		setRejectedPastMeeting(false);
		if (!isAppointment) {
			onNavigateMeetRoom();
		}
	};

	const onInitialized = meetingId => {
		emit(
			SOCKET_EVENTS.EMIT.USER_CONNECTED,
			{
				meetingId: meetingId
			},
			handleEmitWithRequestTracking
		);
	};

	const onCancelSearch = () => {
		setCallState(CALL_STATE_TYPES.DEFAULT);
		setIsAcceptedCall(false);
		setIsAcceptedCallLoading(false);
		resetSocketData();

		offStatesExcept([]);
		emit(SOCKET_EVENTS.EMIT.SEARCH_ABORTED, {}, handleEmitWithRequestTracking);
	};

	const onLeaveCall = (isMeetCamera = false, meetingReason = MEETING_REASON_TYPES.ENDED_BY_EXPERT) => {
		queryClient.refetchQueries(QUERY_KEYS.findAppointmentsOfTheDay);

		emit(
			SOCKET_EVENTS.EMIT.MEETING_TERMINATION_REQUESTED,
			{
				meetingId: socketData?.connectionData?.meetingId || appointmentInformation?.meetingId,
				meetingReason: meetingReason,
				expertDuration: 0,
				studentDuration: 0
			},
			socketCallback.handleMeetingTerminationRequested
		);

		/*
	if (isMeetCamera) {

	}
	 */
	};

	useEffect(() => {
		if (isAppointmentAvailable) {
			initConnectedListener();
		}
	}, [isAppointmentAvailable]);

	const onExtensionSubmitted = () => {
		emit(
			SOCKET_EVENTS.EMIT.MEETING_EXTENSION_SUBMITTED,
			{
				meetingId: socketData?.connectionData?.meetingId || socketData?.meetingCreated?.meetingId,

				confirmationType: CONFIRMATION_TYPES.ACCEPTED
			},
			handleEmitWithRequestTracking
		);
	};

	const handleNoResponseMatch = () => {
		emit(
			SOCKET_EVENTS.EMIT.MATCH_CONFIRMED,
			{
				confirmationType: CONFIRMATION_TYPES.NO_RESPONSE
			},
			handleEmitWithRequestTracking
		);

		setIsSearchLoading(false);

		setCallState(CALL_STATE_TYPES.DEFAULT);
	};

	const onNavigateMeetRoom = (): void => {
		const entity = {
			userInfos: {
				studentName: socketData?.handleMatch?.studentName,
				studentAvatar: socketData?.handleMatch?.studentAvatarUrl,
				coachName: socketData?.handleMatch?.expertName,
				coachAvatar: socketData?.handleMatch?.expertAvatarUrl,
				coachId: socketData?.handleMatch?.expertId,
				coachSubject: socketData?.handleMatch?.meetingSubject?.[0]?.subjectName
			},
			meetingId: socketData?.meetingCreated?.meetingId,
			streamId: socketData?.meetingCreated?.providerParticipantId,
			roomId: socketData?.meetingCreated?.roomId,
			token: socketData?.meetingCreated?.token,
			socketURL: socketData?.meetingCreated?.videoCallProviderInfo?.host,
			providerName: socketData?.meetingCreated?.videoCallProviderInfo?.name,
			endDate: socketData?.meetingCreated?.endDate,
			extensionNotificationDate: socketData?.meetingCreated?.extensionNotificationDate,
			interactionId: socketData?.meetingCreated?.interactionId,
			meetingLastConnectionDate: socketData?.meetingCreated?.meetingLastConnectionDate
		};

		setSocketData((prevState: any) => ({
			...prevState,
			connectionData: entity
		}));

		setCallState(CALL_STATE_TYPES.CONNECT);
		meetCameraRef.current?.init?.(entity);
	};

	const onNavigateAppointmentMeetRoom = (values: Record<string, any>) => {
		const entity = {
			userInfos: {
				studentName: values?.studentName,
				studentAvatar: values?.studentAvatar,
				coachName: values?.coachName,
				coachAvatar: values?.coachAvatar,
				coachId: values?.coachId,
				coachSubject: 'Uzman'
			},
			meetingId: values?.meetingId,
			streamId: values?.providerParticipantId,
			roomId: values?.roomId,
			token: values?.token,
			socketURL: values?.videoCallProviderInfo?.host,
			providerName: values?.videoCallProviderInfo?.name,
			endDate: values?.endDate,
			extensionNotificationDate: values?.extensionNotificationDate,
			interactionId: values?.interactionId,
			connectionApp: values?.liveServiceGroup || CALL_SERVICE_TYPES.COACHING,
			meetingLastConnectionDate: values?.meetingLastConnectionDate
		};

		setConnectionApp(values?.liveServiceGroup);
		setAppointmentInformation(values);
		setSocketData((prevState: any) => ({
			...prevState,
			connectionData: entity
		}));

		onStartCall(true, values?.meetingId);

		meetCameraRef.current?.init?.(entity);
	};

	useEffect(() => {
		if (activeMeeting?.meeting?.roomId && callState !== CALL_STATE_TYPES.CONNECT && callState !== CALL_STATE_TYPES.FOUND) {
			setShowPastMeetingModal(true);

			const expertMeetingParticipant = activeMeeting?.meetingParticipantList?.find(meet => meet.participantType === 'EXPERT');

			if (expertMeetingParticipant && expertMeetingParticipant?.connectionStatus === CONNECTION_STATUS.NOT_CONNECTED) {
				setPastMeetingModalTexts({
					title: 'Görüşmeye Bağlan',
					description: 'Görüşmen başladı. Bağlan butonuna tıklayarak görüşmene bağlanabilirsin.'
				});
			} else {
				setPastMeetingModalTexts({
					title: 'Görüşmen Yarıda Kaldı',
					description: 'Bağlan butonuna basarak görüşmeye devam edebilirsin.'
				});
			}

			setHasActiveMeeting(true);
		} else if (!activeMeeting?.meeting?.roomId) {
			setHasActiveMeeting(false);
		}
	}, [activeMeeting]);

	const onConnectPastMeeting = () => {
		const formattedData = formatMeetingData(activeMeeting);

		setSocketData((prevState: any) => ({
			...prevState,
			connectionData: formattedData,
			meetingCreated: {
				meetingId: activeMeeting?.meeting?.id,
				...activeMeeting?.meeting
			}
		}));

		initConnectedListener();
		emit(
			SOCKET_EVENTS.EMIT.USER_CONNECTED,
			{
				meetingId: activeMeeting?.meeting?.id
			},
			handleEmitWithRequestTracking
		);

		setMeetingType(formattedData?.meetingType);
		setConnectionApp(formattedData?.connectionApp);
		meetCameraRef.current?.init?.(formattedData);
		setCallState(CALL_STATE_TYPES.CONNECT);
		setShowPastMeetingModal(false);
		setRejectedPastMeeting(false);
	};

	const onCancelConnectPastMeeting = () => {
		setCallState(CALL_STATE_TYPES.FOUND);
		setIsAcceptedCall(true);
		setIsAcceptedCallLoading(false);
		initConnectedListener();

		const studentMeetingParticipant = activeMeeting?.meetingParticipantList?.find(meet => meet.participantType === 'STUDENT');

		setSocketData((prevState: any) => ({
			...prevState,
			handleMatch: {
				...activeMeeting,
				studentAvatarUrl: activeMeeting?.studentAvatarLink,
				studentId: studentMeetingParticipant?.participantId
			}
		}));

		setConnectionApp(activeMeeting?.meeting?.liveServiceGroup);
		setShowPastMeetingModal(false);
		setRejectedPastMeeting(true);
	};

	const onShowStudentNotConnect = (): void => {
		const meetingId = socketData?.meetingCreated?.meetingId || socketData?.connectionData?.meetingId;

		emit(SOCKET_EVENTS.EMIT.COUNTERPART_PARTICIPANT_STATUS_REQUESTED, {
			meetingId
		});

		on(SOCKET_EVENTS.ON.PARTICIPANT_STATUS_RETRIEVED_EVENT, data => {
			const dataParsed = jsonTryParse(data);

			if (dataParsed?.counterpartParticipantStatus === 'NOT_CONNECTED') {
				setStudentNotConnectModal(true);
			}
		});
	};

	const handleStudentNotConnected = (shouldRecall: boolean) => {
		isReCallRef.current = shouldRecall;

		setStudentNotConnectModal(false);
		showStudentNotConnectModalRef.current = false;

		onLeaveCall(MEETING_REASON_TYPES.ENDED_BY_SYSTEM);
	};

	useEffect(() => {
		on(SOCKET_EVENTS.ON.STUDENT_JOINED, data => {
			const dataParsed = jsonTryParse(data);

			if (callStateRef.current !== CALL_STATE_TYPES.CONNECT) {
				setConnectedUserData(dataParsed);
				setStudentConnectedModal(true);
			}
		});
	}, []);

	const onNavigateConnectedStudentModal = () => {
		setStudentConnectedModal(false);
		if (connectedUserData?.meetingType === MEETING_TYPE.APPOINTMENT) {
			meetAppointmentToastRef?.current?.startCall();

			return;
		}

		onNavigateMeetRoom();
	};

	const getStudentConnectedStartDate = () => {
		if (connectedUserData?.meetingType !== MEETING_TYPE.APPOINTMENT) {
			return (
				<>
					<span className={'date'}>{formatCustomDate(connectedUserData?.startDate, 'date')}</span>
					<span className={'hour'}>{formatCustomDate(connectedUserData?.startDate, 'time')}</span>
				</>
			);
		}

		return (
			<>
				<span className={'hour'}>{formatCustomDate(connectedUserData?.startDate, 'time')}</span>
			</>
		);
	};

	on(SOCKET_EVENTS.ON.APPOINTMENT_CREATED, () => {
		queryClient.removeQueries({ queryKey: [QUERY_KEYS.findAppointmentsOfTheDay] });
	});

	on(SOCKET_EVENTS.ON.APPOINTMENT_COMPLETED, () => {
		queryClient.removeQueries({ queryKey: [QUERY_KEYS.findAppointmentsOfTheDay] });
	});

	on(SOCKET_EVENTS.ON.APPOINTMENT_CANCELLED, () => {
		queryClient.removeQueries({ queryKey: [QUERY_KEYS.findAppointmentsOfTheDay] });
	});

	return (
		<MeetingContext.Provider
			value={{
				callState,
				isAcceptedCall,
				socketData,
				handleSearchUser,
				onApprove,
				onStartCall,
				onCancelSearch,
				onExtensionSubmitted,
				connectionApp,
				onNavigateAppointmentMeetRoom,
				onLeaveCall,
				onConnectPastMeeting,
				handleNoResponseMatch,
				hasActiveMeeting,
				onShowStudentNotConnect,
				meetingType,
				setMeetingType,
				setActiveAppointment,
				activeAppointment,
				setIsAppointmentAvailable,
				isAppointmentAvailable,
				isAcceptedCallLoading,
				isSearchLoading,
				showPastMeetingModal,
				studentConnectedModal,
				isConnectToastVisible
			}}>
			{children}
			<MeetAppointmentToast
				ref={meetAppointmentToastRef}
				isVisible={isAppointmentAvailable && (!hasActiveMeeting || rejectedPastMeeting) && callStateRef.current !== CALL_STATE_TYPES.CONNECT && isAppointmentConnectToastVisible}
				appointment={activeAppointment}
			/>
			<MeetCamera
				ref={meetCameraRef}
				connectionApp={connectionApp}
				onInitialized={onInitialized}
			/>
			<MeetToast isVisible={isConnectToastVisible && !isAppointmentAvailable} />

			<DialogModal
				isOpen={showPastMeetingModal}
				onClose={onCancelConnectPastMeeting}
				variant="error"
				title={pastMeetingModalTexts?.title}
				primaryButtonText={'Bağlan'}
				secondaryButtonText="Vazgeç"
				onPrimaryClick={() => controlPermission(onConnectPastMeeting)}
				onSecondaryClick={onCancelConnectPastMeeting}
				showCloseIcon={false}>
				<p>{pastMeetingModalTexts?.description}</p>
			</DialogModal>
			<DialogModal
				isOpen={showStudentNotConnectModal}
				onClose={() => setStudentNotConnectModal(false)}
				variant="info"
				CustomIcon={
					<img
						src={NoStudentIcon}
						width={40}
						height={40}
					/>
				}
				title="Öğrenci Görüşmeye Katılmadı!"
				shouldCloseOnOverlayClick={false}
				className="student-not-connect-modal"
				showCloseIcon={false}>
				<p className="modal-text">Öğrenci görüşmeye katılamadığı için görüşmeyi sonlandırabilir veya yeni öğrenci aramaya devam edebilirsiniz.</p>
				<LeaveCountdownButton onClose={() => handleStudentNotConnected(false)} />
			</DialogModal>
			<DialogModal
				isOpen={studentWaitButNotConnectModal}
				onClose={() => {
					setStudentWaitButNotConnectModal(false);
					setStudentWaitButNotConnectStudentId(null);
				}}
				variant="warning"
				title="Görüşme Sonlandırıldı!"
				shouldCloseOnOverlayClick={false}
				className="expert-not-connect-modal"
				showCloseIcon={false}
				onPrimaryClick={() => {
					setStudentWaitButNotConnectModal(false);
					navigate(NavigationPath.messages, {
						state: {
							studentId: studentWaitButNotConnectStudentId
						}
					});
					setStudentWaitButNotConnectStudentId(null);
				}}
				primaryButtonText="Öğrencine Mesaj Gönder">
				<p className="modal-text">Öğrencin seni bekledi ancak görüşmeye katılmadığın için görüşme sonlandırıldı.</p>
			</DialogModal>

			<DialogModal
				isOpen={studentConnectedModal}
				onClose={() => setStudentConnectedModal(false)}
				CustomIcon={
					<img
						src={videoCameraRecord}
						width={40}
						height={40}
					/>
				}
				className="student-connected-modal-wrapper">
				<div className={'scm-container'}>
					<p className={'scm-container-title'}>Görüşmeye Bağlan</p>
					<span className={'scm-container-description'}>Öğrencin görüşmede seni bekliyor."Görüşmeye Bağlan" butonuna tıklayarak görüşmeni başlat!</span>

					{connectedUserData?.studentAvatarUrl && (
						<div className={'meeting-information-container'}>
							<div className={'meeting-information-user-container'}>
								<div>
									<img
										className={'meeting-information-user-img'}
										src={connectedUserData?.studentAvatarUrl}
										alt={connectedUserData?.studentName}
									/>
								</div>
								<div>
									<p>{`${connectedUserData?.studentName} ${connectedUserData?.studentSurname}`}</p>
									<span>{connectedUserData?.studentPreferredGradeName}</span>
								</div>
							</div>

							<div className={'scm-meeting-information-container'}>{getStudentConnectedStartDate()}</div>
						</div>
					)}

					<Button
						variant={'success'}
						fullWidth={true}
						className={'scm-connected-button'}
						onClick={() => controlPermission(onNavigateConnectedStudentModal)}>
						<p>Görüşmeye Bağlan</p>
					</Button>
				</div>
			</DialogModal>
		</MeetingContext.Provider>
	);
};
